/**
 * ═══════════════════════════════════════════════════════════════
 * FEATURE FLAGS & PLAN CONFIGURATION
 * ═══════════════════════════════════════════════════════════════
 * 
 * Centralized configuration for:
 * - Feature flags
 * - Plan variations
 * - Pricing configurations
 * - Product IDs
 * 
 * This allows easy A/B testing and plan changes without code changes.
 */

// ═══════════════════════════════════
// ENVIRONMENT
// ═══════════════════════════════════
const ENV = process.env.NODE_ENV || 'development';
const IS_PRODUCTION = ENV === 'production';

// ═══════════════════════════════════
// FEATURE FLAGS
// ═══════════════════════════════════
export const FEATURES = {
  // Free trial system
  FREE_TRIALS_ENABLED: true,
  FREE_TRIAL_DAYS: 14,
  FREE_TRIAL_NO_CARD: true,

  // Subscription features
  STRIPE_TRIALS_ENABLED: true,
  STRIPE_TRIAL_DAYS: 7,

  // Plan variations
  SHOW_STARTER_PLAN: true,
  SHOW_PRO_PLAN: true,
  SHOW_ENTERPRISE_PLAN: false, // Coming soon

  // TSE features
  TSE_ENABLED: true,
  TSE_CUSTOM_CREDITS: true,

  // ATS features
  ATS_ENABLED: true,
  ATS_FILTERING: true,
  ATS_ANALYTICS: true,
  CALENDAR_ENABLED: true, // ✅ Calendar integration feature

  // Learning Pathways
  LP_ENABLED: true,

  // UI features
  SHOW_TRIAL_BANNER: true,
  SHOW_TRIAL_COUNTDOWN: true,
  URGENT_WARNING_DAYS: 2, // Show urgent warning when <= 2 days left
};

// ✅ Export CALENDAR_ENABLED separately for backward compatibility
export const CALENDAR_ENABLED = FEATURES.CALENDAR_ENABLED;

// ═══════════════════════════════════
// ATS PRICING PLANS (NEW STRUCTURE)
// ═══════════════════════════════════
// ⚠️ TODO: Replace placeholder Stripe price IDs with actual IDs from Stripe Dashboard
export const ATS_PLANS = {
  ats_small: {
    id: 'ats_small',
    name: 'Small Teams',
    displayName: 'ATS - Small Teams',
    description: 'For teams up to 3 seats',
    priceMonthly: 350,
    priceYearly: 3500, // 350 * 10 (save 2 months)
    userRange: 'Up to 3 seats',
    displayFeatures: [
      'Hiring pipelines & customizable stages',
      'Job posting',
      'AI Applicant screening & analysis',
      'Team collaboration',
      'In-app messaging to candidates',
      'Email + chat support',
    ],
    features: {
      activeJobs: 'unlimited',
      users: 3,
      analytics: false, // Analytics available from Medium Teams and up
      support: 'email_chat',
      aiScreening: true,
      jobPosting: true,
      teamCollaboration: true,
      inAppMessaging: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1SQozILC0JuUsTHo78KPi76U',
      yearly: 'price_1SQozILC0JuUsTHoekiqYIgg',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR ATS - Small Teams',
    },
  },

  ats_medium: {
    id: 'ats_medium',
    name: 'Medium Teams',
    displayName: 'ATS - Medium Teams',
    description: 'Most Popular • up to 20 seats',
    priceMonthly: 600,
    priceYearly: 6000, // 600 * 10
    userRange: 'Up to 20 seats',
    displayFeatures: [
      'Everything in Small, plus:',
      'Analytics: time-to-hire, pipeline funnel, source quality',
      'AI contract templates',
      'Onboarding checklists & automations',
    ],
    features: {
      activeJobs: 'unlimited',
      users: 20,
      analytics: true, // Analytics: time-to-hire, pipeline funnel, source quality
      support: 'priority',
      aiScreening: true,
      jobPosting: true,
      teamCollaboration: true,
      inAppMessaging: true,
      aiContracts: true,
      onboardingAutomations: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1SQpGFLC0JuUsTHoUeRn9VjQ',
      yearly: 'price_1SQpGFLC0JuUsTHoh2XDz2Cx',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR ATS - Medium Teams',
    },
  },

  ats_large: {
    id: 'ats_large',
    name: 'Large Teams',
    displayName: 'ATS - Large Teams',
    description: 'For high volume • 50+ seats',
    priceMonthly: 1000,
    priceYearly: 10000, // 1000 * 10
    userRange: '50+ seats',
    displayFeatures: [
      'Everything in Medium, plus:',
      'Auto-created & auto-graded assessments',
      'Resume import & bulk upload',
      'AI knowledge base for culture fit & company context',
    ],
    features: {
      activeJobs: 'unlimited',
      users: 50,
      analytics: true, // Advanced analytics
      support: 'dedicated',
      aiScreening: true,
      jobPosting: true,
      teamCollaboration: true,
      inAppMessaging: true,
      aiContracts: true,
      onboardingAutomations: true,
      autoAssessments: true,
      bulkUpload: true,
      aiKnowledgeBase: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1SQpHGLC0JuUsTHosdS2EkOi',
      yearly: 'price_1SQpHGLC0JuUsTHoVh59pLfx',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR ATS - Large Teams',
    },
  },

  ats_custom: {
    id: 'ats_custom',
    name: 'Custom',
    displayName: 'ATS - Custom',
    description: 'Tailored for your needs',
    priceMonthly: null,
    priceYearly: null,
    userRange: 'Custom',
    contactSales: true,
    features: {
      activeJobs: 'unlimited',
      users: 'unlimited',
      analytics: true,
      support: 'dedicated',
      volumePricing: true,
      implementation: true,
      migration: true,
      hrisSetup: true,
      stripeTrial: false,
      freeTrialEligible: false,
    },
  },
};

// ═══════════════════════════════════
// TSE PRICING PLANS (NEW STRUCTURE)
// ═══════════════════════════════════
// ⚠️ TODO: Replace placeholder Stripe price IDs with actual IDs from Stripe Dashboard
export const TSE_PLANS = {
  tse_basic: {
    id: 'tse_basic',
    name: 'Basic',
    displayName: 'TSE - Basic',
    description: 'Includes 250 contact credits/month',
    credits: 250,
    priceMonthly: 120,
    priceYearly: 1200, // 120 * 10 (save 2 months)
    displayFeatures: [
      '800M+ profiles from multiple sources',
      'AI-ranked candidate matching',
      'Direct contact info (email & phone)',
      '250 contact info unlocks / month',
      'Shortlists & CSV export',
    ],
    features: {
      profiles: '800M+',
      aiMatching: true,
      directContact: true,
      creditsPerMonth: 250,
      shortlists: true,
      csvExport: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1Sm66YLC0JuUsTHo8S9LAwEV',
      yearly: 'price_1Sm66YLC0JuUsTHoGSFMCIVx',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR TSE - Basic',
    },
  },

  tse_professional: {
    id: 'tse_professional',
    name: 'Professional',
    displayName: 'TSE - Professional',
    description: 'Includes 1,000 contact credits/month',
    credits: 1000,
    priceMonthly: 180,
    priceYearly: 1800, // 180 * 10
    displayFeatures: [
      'Everything in Basic, plus:',
      '1,000 contact info unlocks / month',
      'Early access to autonomous candidate outreach',
    ],
    features: {
      profiles: '800M+',
      aiMatching: true,
      directContact: true,
      creditsPerMonth: 1000,
      shortlists: true,
      csvExport: true,
      stripeTrial: false,
      freeTrialEligible: true,
      autonomousOutreachEarlyAccess: true,
    },
    stripe: {
      monthly: 'price_1Sm67SLC0JuUsTHoc44vNMqC',
      yearly: 'price_1Sm67SLC0JuUsTHoE1u5Lmf8',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR TSE - Professional',
    },
  },

  // NOTE: tse_enterprise plan REMOVED (Jan 2026) - use tse_custom for "Talk to Sales"

  tse_custom: {
    id: 'tse_custom',
    name: 'Custom',
    displayName: 'TSE - Custom',
    description: 'Tailored',
    credits: 'unlimited',
    priceMonthly: null,
    priceYearly: null,
    contactSales: true,
    features: {
      profiles: '800M+',
      aiMatching: true,
      directContact: true,
      creditsPerMonth: 'unlimited',
      shortlists: true,
      csvExport: true,
      apiAccess: true,
      integrations: true,
      onboarding: true,
      stripeTrial: false,
      freeTrialEligible: false,
    },
  },
};

// ═══════════════════════════════════
// BUNDLE PRICING PLANS (NEW)
// ═══════════════════════════════════
// ⚠️ TODO: Replace placeholder Stripe price IDs with actual IDs from Stripe Dashboard
export const BUNDLE_PLANS = {
  bundle_small: {
    id: 'bundle_small',
    name: 'Small Teams',
    displayName: 'Bundle - Small Teams',
    description: 'Save vs separate • up to 3 seats',
    priceMonthly: 405,
    priceYearly: 4050, // 405 * 10 (save 2 months)
    userRange: 'Up to 3 seats',
    displayFeatures: [
      'All ATS Small features',
      'All TSE Basic features (250 profile credits/user/mo)',
      'Source candidates AND manage your pipeline',
      'Co-term billing & unified reporting',
    ],
    includes: {
      ats: 'ats_small',
      tse: 'tse_basic',
    },
    features: {
      // ATS Small features - EXPLICITLY SET ALL FEATURES
      activeJobs: 'unlimited',
      users: 3,
      analytics: false,
      support: 'email_chat',
      aiScreening: true,
      jobPosting: true,
      teamCollaboration: true,
      inAppMessaging: true,
      // TSE Basic features
      tseCredits: 250,
      // Bundle-specific
      coTermBilling: true,
      unifiedReporting: true,
      sharedCandidates: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1SQpIkLC0JuUsTHoYK80HX7F',
      yearly: 'price_1SQpIkLC0JuUsTHovrREQptC',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR Bundle - Small Teams (ATS + TSE)',
    },
  },

  bundle_medium: {
    id: 'bundle_medium',
    name: 'Medium Teams',
    displayName: 'Bundle - Medium Teams',
    description: 'Best Value • up to 20 seats',
    priceMonthly: 630,
    priceYearly: 6300, // 630 * 10
    userRange: 'Up to 20 seats',
    displayFeatures: [
      'All ATS Medium features',
      'All TSE Professional features (1000 profile credits/user/mo)',
      'Analytics + AI contracts + onboarding',
      'Priority support',
    ],
    includes: {
      ats: 'ats_medium',
      tse: 'tse_professional',
    },
    features: {
      // ATS Medium features - EXPLICITLY SET ALL FEATURES
      activeJobs: 'unlimited',
      users: 20,
      analytics: true,
      support: 'priority',
      aiScreening: true,
      jobPosting: true,
      teamCollaboration: true,
      inAppMessaging: true,
      aiContracts: true, // ✅ AI Contracts unlocked
      onboardingAutomations: true,
      // TSE Professional features
      tseCredits: 1000,
      // Bundle-specific
      coTermBilling: true,
      unifiedReporting: true,
      sharedCandidates: true,
      automations: true,
      deeperAnalytics: true,
      prioritySupport: true,
      dedicatedOnboarding: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1SQpJgLC0JuUsTHojSp4VW3T',
      yearly: 'price_1SQpJgLC0JuUsTHo5eg72m9n',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR Bundle - Medium Teams (ATS + TSE)',
    },
  },

  bundle_large: {
    id: 'bundle_large',
    name: 'Large Teams',
    displayName: 'Bundle - Large Teams',
    description: 'For scale • 50+ seats',
    priceMonthly: 990,
    priceYearly: 9900, // 990 * 10
    userRange: '50+ seats',
    displayFeatures: [
      'All ATS Large features',
      'All TSE Professional features (1000 profile credits/user/mo)',
      'Auto assessments + bulk upload + AI knowledge base',
      'Dedicated account manager',
    ],
    includes: {
      ats: 'ats_large',
      tse: 'tse_professional',
    },
    features: {
      // ATS Large features - EXPLICITLY SET ALL FEATURES
      activeJobs: 'unlimited',
      users: 50,
      analytics: true,
      support: 'dedicated',
      aiScreening: true,
      jobPosting: true,
      teamCollaboration: true,
      inAppMessaging: true,
      aiContracts: true, // ✅ AI Contracts unlocked
      onboardingAutomations: true,
      autoAssessments: true, // ✅ Auto Assessments unlocked
      bulkUpload: true, // ✅ Bulk Upload unlocked
      aiKnowledgeBase: true,
      // TSE Professional features
      tseCredits: 1000,
      // Bundle-specific
      coTermBilling: true,
      unifiedReporting: true,
      sharedCandidates: true,
      automations: true,
      advancedReporting: true,
      integrationSupport: true,
      quarterlyReviews: true,
      stripeTrial: false,
      freeTrialEligible: true,
    },
    stripe: {
      monthly: 'price_1SQpKjLC0JuUsTHoXTsi1IyH',
      yearly: 'price_1SQpKjLC0JuUsTHok5DGvp6d',
    },
    metadata: {
      subscriptionName: 'JOBSEEQR Bundle - Large Teams (ATS + TSE)',
    },
  },

  bundle_custom: {
    id: 'bundle_custom',
    name: 'Custom',
    displayName: 'Bundle - Custom',
    description: 'Tailored',
    priceMonthly: null,
    priceYearly: null,
    userRange: 'Custom',
    contactSales: true,
    features: {
      customMix: true,
      consolidatedBilling: true,
      integrationSupport: true,
      onboardingSupport: true,
      dedicatedSuccess: true,
      stripeTrial: false,
      freeTrialEligible: false,
    },
  },
};

// ═══════════════════════════════════
// LEARNING PATHWAYS PRICING
// ═══════════════════════════════════
export const LP_PLANS = {
  standard: {
    id: 'learning_pathway',
    name: 'Learning Pathway',
    price: 12,
    stripe: {
      priceId: IS_PRODUCTION
        ? 'price_1RyqzgLC0JuUsTHoe6mDNFMh'  // Production price ID
        : 'price_test_1234567890',           // Test price ID
    },
    features: [
      'CV gap analysis',
      'Personalized learning plan',
      'Earn certifications',
      'Lifetime access',
    ],
  },
};

// ═══════════════════════════════════
// PRODUCT ID TO PLAN ID MAPPING
// ═══════════════════════════════════
// Maps Stripe Product IDs to internal Plan IDs
export const PRODUCT_ID_TO_PLAN_ID = {
  // TSE Plans - New pricing (Jan 2026)
  'prod_TjZFusqjbVH9cb': 'tse_basic',      // Basic $120/mo, 250 credits
  'prod_TjZFlLg6dZsqSY': 'tse_professional', // Pro $180/mo, 1000 credits
  // TSE Plans (legacy - keep for backward compatibility)
  'prod_SEHjjoHmTDR8ff': 'tse_basic',
  'prod_SEGv4UBxv8c8dO': 'tse_basic',
  'prod_RWqK0BG2RpWQU6': 'tse_professional',
  'prod_SEGuhuQMpbAVzs': 'tse_professional',
  // Old enterprise mapped to custom now (deprecated)
  'prod_SEGxvBCOQFq6JZ': 'tse_custom',
  'prod_SEGyYBjJoANkZn': 'tse_custom',

  // ATS Plans
  'prod_TNa9mcw07a4Q4i': 'ats_small',
  'prod_TNaRRIJtMa6eRV': 'ats_medium',
  'prod_TNaSWdkdoy2LLZ': 'ats_large',

  // Bundle Plans
  'prod_TNaTqtj38r78Lh': 'bundle_small',
  'prod_TNaUTRtogfr0uF': 'bundle_medium',
  'prod_TNaVEXB1bZhL1E': 'bundle_large',
};

/**
 * Get planId from Stripe productId
 * @param {string} productId - Stripe product ID
 * @returns {string|null} - Internal plan ID or null
 */
export const getPlanIdFromProductId = (productId) => {
  return PRODUCT_ID_TO_PLAN_ID[productId] || null;
};

// ═══════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════

/**
 * Get plan by ID
 */
export const getPlanById = (productType, planId) => {
  const plans = {
    ats: ATS_PLANS,
    tse: TSE_PLANS,
    bundle: BUNDLE_PLANS,
    learning_pathway: LP_PLANS,
  };

  return plans[productType]?.[planId] || null;
};

/**
 * Get all plans for a product type
 */
export const getPlansForProduct = (productType) => {
  const plans = {
    ats: Object.values(ATS_PLANS),
    tse: Object.values(TSE_PLANS),
    bundle: Object.values(BUNDLE_PLANS),
    learning_pathway: Object.values(LP_PLANS),
  };

  return plans[productType] || [];
};

/**
 * Get Stripe price ID for a plan
 */
export const getStripePriceId = (productType, planId, billingPeriod = 'monthly') => {
  const plan = getPlanById(productType, planId);
  if (!plan) return null;

  if (productType === 'learning_pathway') {
    return plan.stripe?.priceId;
  }

  return plan.stripe?.[billingPeriod] || plan.stripe?.monthly;
};

/**
 * Check if feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  return FEATURES[featureName] === true;
};

/**
 * Check if user can start free trial (UNIFIED)
 */
export const canStartFreeTrial = () => {
  // Unified trial is always available if feature is enabled
  return isFeatureEnabled('FREE_TRIALS_ENABLED');
};

/**
 * Get trial duration in days
 */
export const getTrialDays = (trialType = 'free') => {
  if (trialType === 'free') {
    return FEATURES.FREE_TRIAL_DAYS || 14;
  }
  return FEATURES.STRIPE_TRIAL_DAYS || 7;
};

/**
 * Format price for display
 */
export const formatPrice = (amount, period = 'mo') => {
  if (amount === null || amount === undefined) return 'Contact Sales';
  if (amount === 'unlimited') return 'Unlimited';
  return `$${amount}/${period}`;
};

/**
 * Calculate yearly price with discount
 */
export const calculateYearlyPrice = (monthlyPrice, discountPercent = 10) => {
  if (!monthlyPrice) return null;
  return Math.round(monthlyPrice * 12 * (1 - discountPercent / 100));
};

/**
 * Get plan metadata for Stripe
 */
export const getPlanMetadata = (productType, planId, billingPeriod = 'monthly') => {
  const plan = getPlanById(productType, planId);
  if (!plan) return {};

  const metadata = {
    productType,
    planId,
    billingPeriod,
  };

  if (plan.metadata) {
    if (typeof plan.metadata === 'string') {
      metadata.subscriptionName = plan.metadata;
    } else {
      metadata.subscriptionName = plan.metadata[billingPeriod] || plan.metadata.monthly || plan.name;
    }
  } else {
    metadata.subscriptionName = plan.name;
  }

  return metadata;
};

/**
 * Check if plan requires job count limit
 */
export const requiresJobCountCheck = (planId) => {
  const plan = ATS_PLANS[planId];
  return plan?.features?.activeJobs !== 'unlimited';
};

/**
 * Get maximum jobs for plan
 */
export const getMaxJobsForPlan = (planId) => {
  const plan = ATS_PLANS[planId];
  const activeJobs = plan?.features?.activeJobs;

  if (activeJobs === 'unlimited') return Infinity;
  if (typeof activeJobs === 'number') return activeJobs;
  return 0;
};

/**
 * A/B test helper - randomly assign variant
 */
export const getABTestVariant = (testName, variants = ['A', 'B']) => {
  // Use localStorage to persist variant for user
  const storageKey = `ab_test_${testName}`;
  const existing = localStorage.getItem(storageKey);

  if (existing && variants.includes(existing)) {
    return existing;
  }

  const variant = variants[Math.floor(Math.random() * variants.length)];
  localStorage.setItem(storageKey, variant);
  return variant;
};

/**
 * Check if user should see new pricing
 * (Example of feature flag for gradual rollout)
 */
export const shouldShowNewPricing = () => {
  // Could be based on:
  // - User percentage rollout
  // - Specific user IDs
  // - Company size
  // - Geographic location
  // For now, use environment
  return !IS_PRODUCTION; // Only in development for testing
};

export default {
  FEATURES,
  ATS_PLANS,
  TSE_PLANS,
  BUNDLE_PLANS,
  LP_PLANS,
  CALENDAR_ENABLED,
  getPlanById,
  getPlansForProduct,
  getStripePriceId,
  isFeatureEnabled,
  canStartFreeTrial,
  getTrialDays,
  formatPrice,
  calculateYearlyPrice,
  getPlanMetadata,
  requiresJobCountCheck,
  getMaxJobsForPlan,
  getABTestVariant,
  shouldShowNewPricing,
};
